import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { GRAY_900 } from 'constants/styling/theme'
import { Language } from 'translations/Language'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { timezoneNames } from 'utils/time'
import { useChangeLocale } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component User Language settings displayed in Profile page
 * @example
 * <LanguageSettings />
 */
export const LanguageSettings: React.FC = () => {
  const { t } = useTranslation(['profile', 'language'])
  const { baseUserData } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const changeLocale = useChangeLocale()

  const [currentLanguage, setCurrentLanguage] = useState<Language>()
  const [currentTimezone, setCurrentTimezone] = useState<string>()

  useLayoutEffect(() => {
    changeLocale.reset()
    return () => {
      changeLocale.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    if (baseUserData?.language) setCurrentLanguage(Language[(baseUserData.language as Language)])
  }, [baseUserData])

  useLayoutEffect(() => {
    if (baseUserData?.defaultTimezone) setCurrentTimezone((baseUserData.defaultTimezone))
  }, [baseUserData])

  useEffect(() => {
    if (changeLocale.isSuccess) {
      spawnSuccessToast(t('language.success'))
    }

    if (changeLocale.isError) {
      spawnErrorToast(t('language.error'))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLocale.isSuccess, changeLocale.isError])

  return (
    <Stack gap="1.6rem">
      <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
        {t('language.title')}
      </Typography>

      <BorderBoxWrapper padding={2.4} gap={1.6} display="inline-grid">

        <Stack gap={1.6} flexDirection="row" alignItems="center" justifyContent="space-between">

          <Stack width="100%">
            <MUIDropdown
              button={(isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  label={t('language.label')}
                  placeholder={t('language.select')}
                  value={(t(`language:${(currentLanguage || '').toUpperCase()}`) || '').toString()}
                  onClick={action}
                />
              )}
            >
              {Object.values(Language).map((lang) => (
                <MUIDropdownItem
                  key={lang}
                  onClick={() => {
                    if (!baseUserData?.email) return
                    setCurrentLanguage(lang)
                    changeLocale.mutate({ email: baseUserData.email, language: lang })
                  }}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`language:${lang.toUpperCase()}`, lang)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

          <Stack width="100%">
            <MUIDropdownInput
              readOnly
              isNativeSelect
              id="timezone"
              label={t('timezone.label')}
              placeholder={t('language.select')}
              value={currentTimezone}
              onChange={e => {
                if (!baseUserData?.email) return
                setCurrentTimezone(e.target.value)
                changeLocale.mutate({ email: baseUserData.email, timezone: e.target.value })
              }}
            >
              {timezoneNames.map((timezone) => (
                <option value={timezone} key={timezone}>{timezone}</option>
              ))}
            </MUIDropdownInput>
          </Stack>

        </Stack>

      </BorderBoxWrapper>
    </Stack>
  )
}
